import { useMemo } from 'react'
import { collection, query, where } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { useFirestoreCollection } from './useFirestore'
import { useAuth } from './useAuth'
import { useAdminPendingSuggestionCount } from './useAdminEventSuggestions'
import { useAdminPendingFeedbackCount } from './useAdminFeedback'
import { useAdminPendingRegionRequestCount } from './useAdminRegionRequests'
import { useAdminPendingScheduleLeadCount } from './useAdminScheduleLeads'
import { useAdminPendingSetReportCount } from './useAdminSetReports'
import { useAdminPendingEventReportCount } from './useAdminEventReports'
import { useUnreadFailedRoutineRunCount } from './useAdminRoutineRuns'
import type { Event } from '../types'
import { isRescuable } from '@shared/reviewQueue'

// Draft events in the review queue — the same set AdminReviewPage works through
// (unpublished, not already rejected). Non-admins run no query at all, and a
// read failure leaves the list empty so every count fails closed to 0.
export function useAdminPendingEvents(): { events: Event[]; loading: boolean } {
  const { isAdmin } = useAuth()

  const draftsQuery = useMemo(
    () =>
      isAdmin
        ? (query(
            collection(db, 'events'),
            where('published', '==', false),
          ) as Query<Event>)
        : null,
    [isAdmin],
  )

  const { data, loading } = useFirestoreCollection<Event>(draftsQuery)

  const events = useMemo(() => data.filter(e => !e.needsReview), [data])
  return { events, loading }
}

export function useAdminPendingEventCount(): number {
  return useAdminPendingEvents().events.length
}

// The drafts the review-queue rescue will never fix (dead source, no source to
// re-fetch, or a reason a re-render can't clear) — the only ones an admin has
// to act on.
export function useAdminStuckEventCount(): number {
  const { events } = useAdminPendingEvents()
  return useMemo(
    () => events.filter(e => !isRescuable(e.reviewReasons ?? [], e)).length,
    [events],
  )
}

// Everything waiting on an admin's decision, for the nav chrome badge: event
// suggestions the seeding routine hasn't resolved yet, untriaged feedback,
// untriaged area requests, untriaged schedule leads, set and event reports the
// routines left for a person, and failed routine runs
// nobody has looked at. Draft events are left out — the rescue routine works
// that queue on its own. The per-tool rows in AdminView use the counts
// separately so none is double-reported.
export function useAdminPendingCount(): number {
  return (
    useAdminPendingSuggestionCount() +
    useAdminPendingFeedbackCount() +
    useAdminPendingRegionRequestCount() +
    useAdminPendingScheduleLeadCount() +
    useAdminPendingSetReportCount() +
    useAdminPendingEventReportCount() +
    useUnreadFailedRoutineRunCount(false)
  )
}
